import { gsap } from 'gsap';
import type { ThreeStage } from '../../../three/ThreeStage';
import { BaseScene } from '../../../core/BaseScene';
import { glyphGroup, type GlyphId } from '../../../components/glyphs';
import { draw, pulse } from '../../../components/svg';
import { words, revealWords, settleWords } from '../../../components/text';
import { productReveal, playProductReveal, settleProductReveal } from '../../../components/reveal';

/** Everyday sustainable actions documented in the whitepaper (CARBONMOVE.md §1, §12). */
const ACTIONS: ReadonlyArray<{ label: string; glyph: GlyphId }> = [
  { label: 'Walking', glyph: 'walk' },
  { label: 'Cycling', glyph: 'bike' },
  { label: 'Public transport', glyph: 'bus' },
  { label: 'Recycling', glyph: 'recycle' },
];
const ROW_Y = 560;
const actionX = (i: number): number => 380 + i * 280;
const IMPACT = { x: 800, y: 250, r: 54 };

const rise = (i: number): string => {
  const x = actionX(i);
  return `M${x} ${ROW_Y - 78} C${x} ${ROW_Y - 200} ${IMPACT.x + (x - IMPACT.x) * 0.25} ${IMPACT.y + 150} ${IMPACT.x + (x - IMPACT.x) * 0.12} ${IMPACT.y + IMPACT.r + 6}`;
};

/**
 * 0 People walking, cycling, taking the bus, recycling — SMALL ACTIONS.
 * 1 Each action adds to something larger — REAL IMPACT.
 * 2 The connection turns uncertain — BUT HOW DO YOU PROVE IT?
 * 3 CarbonMove — Turning verified sustainable action into measurable climate impact.
 */
export class CarbonProofScene extends BaseScene {
  public readonly stepCount = 4;
  private flowing = false;

  protected template(): string {
    const actions = ACTIONS.map((a, i) => {
      const x = actionX(i);
      return `<g class="cp-action" data-index="${i}">
        <path class="cp-rise draw" pathLength="1" d="${rise(i)}"/>
        <text class="cp-question" x="${(x + IMPACT.x) / 2 + (x < IMPACT.x ? -10 : 10)}" y="${(ROW_Y + IMPACT.y) / 2 + 10}" text-anchor="middle">?</text>
        <g transform="translate(${x} ${ROW_Y})"><g class="cp-action__motion">
          <circle class="cp-action__ring" r="70"/>
          <g class="cp-action__glyph">${glyphGroup(a.glyph, 58)}</g>
          <path class="cp-ground" d="M-90 102 H90"/>
          <text class="cp-action__label" y="140" text-anchor="middle">${a.label.toUpperCase()}</text>
        </g></g>
        <circle class="cp-pulse" r="5"/>
      </g>`;
    }).join('');

    return `
      <div class="scene-layout carbon-proof">
        <svg class="diagram cp-diagram" viewBox="0 0 1600 900" role="img" aria-label="Walking, cycling, public transport and recycling adding up to real impact that is hard to prove">
          <g class="cp-impact" data-from="1">
            <circle class="cp-impact__glow" cx="${IMPACT.x}" cy="${IMPACT.y}" r="${IMPACT.r + 40}"/>
            <circle class="cp-impact__ring" cx="${IMPACT.x}" cy="${IMPACT.y}" r="${IMPACT.r}"/>
            <text class="cp-impact__label" x="${IMPACT.x}" y="${IMPACT.y + 6}" text-anchor="middle">IMPACT</text>
          </g>
          ${actions}
        </svg>
        <div class="cp-statements">
          <h2 class="statement" data-from="0" data-until="0">${words('SMALL ACTIONS.')}</h2>
          <h2 class="statement statement--accent" data-from="1" data-until="1">${words('REAL IMPACT.')}</h2>
          <h2 class="statement" data-from="2" data-until="2">${words('BUT HOW DO YOU PROVE IT?')}</h2>
        </div>
        ${productReveal('carbonmove', 3)}
      </div>`;
  }

  protected onEnter(immediate: boolean): void {
    if (immediate || this.step !== 0) return;
    const tl = this.timeline();
    tl.fromTo(this.$$('.cp-action__motion'), { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.25, ease: 'expo.out' }, 0.1);
    tl.add(revealWords(this.$('.cp-statements .statement')), 1.1);
  }

  protected onExit(): void {
    this.flowing = false;
  }

  protected onStep(step: number, stage: ThreeStage, immediate: boolean, previousStep: number): void {
    stage.setAccent(this.meta.accent);
    stage.setMode(this.meta.id, step, immediate);
    const forward = !immediate && step > previousStep;
    const d = immediate ? 0 : 1;
    const tl = this.timeline();

    gsap.set(this.$$('.cp-action__motion'), { opacity: 1, y: 0 });
    this.$$('.cp-statements .statement').forEach((s) => settleWords(s));
    if (forward && step <= 2) tl.add(revealWords(this.$$('.cp-statements .statement')[step]), 0.2);
    this.$('.cp-diagram').dataset.doubt = String(step >= 2);

    // 1 — every action rises into one shared impact.
    const rises = this.$$('.cp-rise');
    const impact = this.$$('.cp-impact circle, .cp-impact text');
    if (forward && step === 1) {
      draw(rises, false, { immediate: true });
      tl.add(draw(rises, true, { duration: 1, stagger: 0.15, ease: 'power2.inOut' }), 0.3);
      tl.fromTo(impact, { opacity: 0, scale: 0.5, svgOrigin: `${IMPACT.x} ${IMPACT.y}` }, { opacity: 1, scale: 1, duration: 1, ease: 'back.out(1.8)' }, 1.1);
    } else {
      draw(rises, step >= 1, { immediate: true });
      gsap.set(impact, { opacity: 1, scale: 1, svgOrigin: `${IMPACT.x} ${IMPACT.y}` });
    }

    // 2 — doubt: every connection gets a question mark.
    const questions = this.$$('.cp-question');
    if (forward && step === 2) tl.fromTo(questions, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.12, ease: 'back.out(2.5)' }, 0.5);
    else gsap.set(questions, { opacity: step === 2 ? 1 : 0, scale: 1, transformOrigin: '50% 50%' });

    // 3 — the reveal takes the centre; the people stay visible behind it.
    gsap.to(this.$('.cp-diagram'), { opacity: step >= 3 ? 0.22 : 1, duration: d * 0.8, ease: 'power2.inOut' });
    if (forward && step === 3) playProductReveal(this.root, tl, 0.4);
    else settleProductReveal(this.root);

    this.syncFlow(step === 1, forward ? 1.4 : 0);
  }

  private syncFlow(on: boolean, delay: number): void {
    if (on === this.flowing) return;
    this.flowing = on;
    this.stopLoops();
    const markers = this.$$<SVGCircleElement>('.cp-pulse');
    markers.forEach((m) => { m.style.opacity = '0'; });
    if (!on) return;
    const paths = this.$$<SVGPathElement>('.cp-rise');
    markers.forEach((m, i) => this.loop(pulse(m, paths[i], { duration: 1.4, delay: delay + i * 0.3, repeatDelay: 0.9 })));
  }
}
